import React, {useContext, useEffect} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import Button from "../../../components/button";
import Card from "../../../components/card";
import TemplateDefault from "../../../components/template-default";
import demandOrganizationService from "../../../services/demand-organization-service";
import {ProjectContext} from "../../../context/project-context";
import DemandBoard from "../components/demand-board";

const DemandOrganizationBoard = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {user: currentUser} = useContext(ProjectContext);
    const {data: boardData, isLoading, error} = useQuery({
        queryKey: ["demand-organization", id],
        queryFn: () => demandOrganizationService.getById(id),
        enabled: !!id,
        retry: 0,
        refetchInterval: 5000,
        refetchIntervalInBackground: true,
    });
    const board = boardData?.data ?? null;
    const isEditing = Boolean(board?.userEditing && String(board.userEditing.id) === String(currentUser?.id));
    const {data: membersData, isLoading: membersLoading, error: membersError} = useQuery({
        queryKey: ["demand-organization-members", id],
        queryFn: () => demandOrganizationService.getMembers(id),
        enabled: !!id,
        retry: 0,
    });
    const boardMembers = Array.isArray(membersData) ? membersData : membersData?.data ?? [];
    const saveStructure = useMutation({
        mutationFn: content => demandOrganizationService.updateContent(content, id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["demand-organization"]});
        },
        onError: () => toast.error("Não foi possível salvar as alterações do quadro."),
    });
    const saveCardDetails = useMutation({
        mutationFn: ({cardReference, changes}) => demandOrganizationService.updateCardDetails(cardReference, changes, id),
        onError: () => toast.error("Não foi possível salvar os detalhes do cartão."),
    });
    const createComment = useMutation({
        mutationFn: ({cardReference, text}) => demandOrganizationService.createCardComment(id, cardReference, text),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["demand-organization", id]}),
        onError: () => toast.error("Não foi possível enviar o comentário."),
    });
    const updateComment = useMutation({
        mutationFn: ({cardReference, commentId, text}) => demandOrganizationService.updateCardComment(id, cardReference, commentId, text),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["demand-organization", id]}),
        onError: () => toast.error("Não foi possível alterar o comentário."),
    });
    const deleteComment = useMutation({
        mutationFn: ({cardReference, commentId}) => demandOrganizationService.deleteCardComment(id, cardReference, commentId),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["demand-organization", id]}),
        onError: () => toast.error("Não foi possível excluir o comentário."),
    });
    const saveLabels = useMutation({
        mutationFn: labels => demandOrganizationService.updateLabels(labels, id),
        onError: () => toast.error("Não foi possível salvar as etiquetas do quadro."),
    });
    const editingLock = useMutation({
        mutationFn: () => demandOrganizationService.startEditing(id),
        onSuccess: data => queryClient.setQueryData(["demand-organization", id], data),
        onError: error => toast.info(error.response?.data?.message ?? "Este quadro está sendo editado por outro usuário."),
    });

    useEffect(() => {
        if (!isEditing) return undefined;

        const heartbeat = window.setInterval(() => {
            demandOrganizationService.startEditing(id)
                .then(data => queryClient.setQueryData(["demand-organization", id], data))
                .catch(() => queryClient.invalidateQueries({queryKey: ["demand-organization", id]}));
        }, 10000);

        return () => window.clearInterval(heartbeat);
    }, [id, isEditing, queryClient]);

    useEffect(() => {
        if (!isEditing) return undefined;
        const release = () => { void demandOrganizationService.stopEditing(id); };
        window.addEventListener("pagehide", release);
        return () => window.removeEventListener("pagehide", release);
    }, [id, isEditing]);

    const stopEditing = async () => {
        try {
            const data = await demandOrganizationService.stopEditing(id);
            queryClient.setQueryData(["demand-organization", id], data);
        } catch {
            queryClient.invalidateQueries({queryKey: ["demand-organization", id]});
        }
    };

    const goBack = async () => {
        if (isEditing) await stopEditing();
        navigate("/activity-control/demands");
    };

    useEffect(() => {
        const requestError = error ?? membersError;
        if (!requestError) return;

        const status = requestError.response?.status ?? requestError.status;
        navigate(status === 403 ? "/403" : "/404", {replace: true});
    }, [error, membersError, navigate]);

    if (isLoading || membersLoading) return <TemplateDefault title="Organização de demandas" isLoading />;

    if (error || membersError || !board) {
        return (
            <TemplateDefault title="Organização de demandas">
                <Card title="Quadro não encontrado">
                    <p className="mb-4">O quadro informado não existe ou foi removido.</p>
                    <Button type="button" onClick={() => navigate("/activity-control/demands")}>Voltar para os quadros</Button>
                </Card>
            </TemplateDefault>
        );
    }

    return (
        <TemplateDefault title={board.name}>
            <div className="h-[calc(100vh-74px)] min-h-[560px]">
                <DemandBoard
                    board={board}
                    initialLists={board.lists ?? []}
                    initialCards={board.cards ?? {}}
                    users={boardMembers}
                    currentUser={currentUser}
                    onBack={goBack}
                    isEditing={isEditing}
                    editingUser={board.userEditing}
                    onStartEditing={() => editingLock.mutate()}
                    onStopEditing={stopEditing}
                    isChangingEditing={editingLock.isPending}
                    onSaveStructure={content => saveStructure.mutateAsync(content)}
                    onSaveCardDetails={(cardReference, changes) => saveCardDetails.mutateAsync({cardReference, changes})}
                    onCreateComment={(cardReference, text) => createComment.mutateAsync({cardReference, text})}
                    onUpdateComment={(cardReference, commentId, text) => updateComment.mutateAsync({cardReference, commentId, text})}
                    onDeleteComment={(cardReference, commentId) => deleteComment.mutateAsync({cardReference, commentId})}
                    onSaveLabels={labels => saveLabels.mutateAsync(labels)}
                    isSaving={saveStructure.isPending || saveCardDetails.isPending || saveLabels.isPending || createComment.isPending || updateComment.isPending || deleteComment.isPending}
                />
            </div>
        </TemplateDefault>
    );
};

export default DemandOrganizationBoard;
